import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Navbar, Footer } from '../../components';
import { submitContactForm } from '../../api/contact';
import styles from './Contact.module.css';

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut', delay } },
});

const PROJECT_TYPES = [
  'Road Construction & Infrastructure',
  'Thermoplastic Road Marking',
  'Traffic Management & Safety',
  'Road Marking & Signage',
  'Smart Traffic & Solar Infrastructure',
  'Consultancy',
  'Other',
];

const EMPTY_FORM = { name: '', org: '', projectType: '', message: '' };

export default function Contact() {
  const [form, setForm]         = useState(EMPTY_FORM);
  const [status, setStatus]     = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (!el) return;
    const timer = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    return () => clearTimeout(timer);
  }, [hash]);

  const isLoading = status === 'loading';

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      await submitContactForm(form);
      setStatus('success');
      setForm(EMPTY_FORM);
    } catch (err) {
      console.error('Contact form error:', err);
      setStatus('error');
      setErrorMsg(err?.text || err?.message || 'Something went wrong. Please try again.');
    }
  };

  const handleReset = () => { setStatus('idle'); setErrorMsg(''); };

  return (
    <div className={styles.page}>
      <Navbar theme="light" cta="Request Consultancy" ctaTo="/contact#consultancy_form" />

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroLabel}>
          <h1 className={styles.heroTitle}>CONTACT US</h1>
        </div>
      </section>

      {/* ── MAIN SECTION ── */}
      <section className={styles.mainSection}>
        {/* Watermark */}
        <div className={styles.watermark} aria-hidden="true">Contact Us</div>

        <div className={styles.mainInner}>

          {/* A — Heading + subtext */}
          <motion.div
            className={styles.leftTop}
            variants={fadeUp(0)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className={styles.mainTitle}>Ready to Build with Purpose?</h2>
            <p className={styles.mainSubtext}>
              Partner with a team that bridges technical mastery with national impact.
              Let's discuss your next infrastructure milestone
            </p>
          </motion.div>

          {/* B — Contact info */}
          <motion.div
            className={styles.contactInfo}
            variants={fadeUp(0.05)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className={styles.infoRow}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Email</span>
                <a href="mailto:info@tektronplus.com" className={styles.infoValue}>
                  info@tektronplus.com
                </a>

              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Contact enquiries</span>
                <a href="mailto:cgo@tektronplus.com" className={styles.infoValue}>
                  cgo@tektronplus.com
                </a>
              </div>
            </div>
            {/*<div className={styles.infoItem}>*/}
            {/*  <span className={styles.infoLabel}>Phone</span>*/}
            {/*  <a href="tel:+16285550147" className={styles.infoValue}>+1 (628) 555-0147</a>*/}
            {/*</div>*/}
          </motion.div>

          {/* C — Form card */}
          <motion.div
            id="consultancy_form"
            className={styles.formCard}
            variants={fadeUp(0.1)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            <AnimatePresence mode="wait">

              {/* Success */}
              {status === 'success' && (
                <motion.div
                  key="success"
                  className={styles.successBox}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                >
                  <span className={styles.successIcon}>✓</span>
                  <h2 className={styles.successTitle}>Request Sent!</h2>
                  <p className={styles.successText}>
                    Thank you for reaching out. Our engineering team will get back to you within 24 hours.
                  </p>
                  <button className={styles.resetBtn} onClick={handleReset}>
                    Send another request
                  </button>
                </motion.div>
              )}

              {/* Form */}
              {status !== 'success' && (
                <motion.div key="form" initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <h2 className={styles.formTitle}>Get In Touch</h2>

                  <form
                    className={`${styles.form} ${isLoading ? styles.formBusy : ''}`}
                    onSubmit={handleSubmit}
                  >
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="name">Full Name</label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        className={styles.input}
                        placeholder="Enter Fullname"
                        value={form.name}
                        onChange={handleChange}
                        disabled={isLoading}
                        required
                      />
                    </div>

                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="org">Org/Agency</label>
                      <input
                        id="org"
                        name="org"
                        type="text"
                        className={styles.input}
                        placeholder="Organization/Agency"
                        value={form.org}
                        onChange={handleChange}
                        disabled={isLoading}
                      />
                    </div>

                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="projectType">Project Type</label>
                      <div className={styles.selectWrapper}>
                        <select
                          id="projectType"
                          name="projectType"
                          className={styles.select}
                          value={form.projectType}
                          onChange={handleChange}
                          disabled={isLoading}
                          required
                        >
                          <option value="" disabled>Select a service type</option>
                          {PROJECT_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                        <svg className={styles.selectCaret} width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                          <path d="M5 7.5L10 12.5L15 7.5" stroke="#404040" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>

                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="message">How can we help?</label>
                      <textarea
                        id="message"
                        name="message"
                        className={styles.textarea}
                        placeholder="Tell us about your project..."
                        rows={5}
                        value={form.message}
                        onChange={handleChange}
                        disabled={isLoading}
                        required
                      />
                    </div>

                    {status === 'error' && (
                      <p className={styles.errorText}>
                        {errorMsg} — or email us at{' '}
                        <a href="mailto:info@tektronplus.com">info@tektronplus.com</a>
                      </p>
                    )}

                    <div className={styles.submitArea}>
                      <button
                        type="submit"
                        className={`${styles.submitBtn} ${isLoading ? styles.submitBtnLoading : ''}`}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <span className={styles.btnSpinner}>
                            <span className={styles.spinner} aria-hidden="true" />
                            Sending…
                          </span>
                        ) : (
                          'Request Consultancy'
                        )}
                      </button>
                      <p className={styles.submitNote}>
                        Expect a response from our engineering team within 24 hours.
                      </p>
                    </div>
                  </form>
                </motion.div>
              )}

            </AnimatePresence>
          </motion.div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
